import { sortBy, groupBy } from 'lodash';

import { getHotelStayTag } from '@apps/main/new-desktop/core/helpers/cancellationPoliciesUtils';
// TODO: modules/ should not depend on external code
import { bookableOptionComparer } from 'libs/utils/src/lib/functions/comparisons';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { getHotelBookableOptionRoomUniqueKey } from '@apps/main/modules/tripPlannerViewModel/ProductAvailabilityViewModel';
import { getFormattedPax } from '@apps/main/new-desktop/core/stores/pax/paxStoreLogic';

import { assertValue } from '@libs/utils';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import type {
  CancellationPolicy,
  HotelBookableAndLockableOption,
  HotelBookableOptionRoom,
  ContentApiImage,
  IngredientId,
  SmartSearchImageUrl,
} from '@libs/trip-planner-interfaces';

import type {
  HotelStaticData,
  HotelDetailsFooterData,
  HotelDetailsGalleryData,
  HotelRoom,
  Stay,
  StayId,
} from '../models';
import { StaySelectionType } from '../models';

const { t } = i18n.global;

// NOTE: Whole solution is ported from old hotel details with some clean code

export function getSelectedStay(stays: Stay[]): Stay | undefined {
  const manuallySelectedStay = stays.find(
    (stay) => stay.selectionType === StaySelectionType.ManuallySelected
  );

  const suggestedStay = stays.find(
    (stay) => stay.selectionType === StaySelectionType.Suggested
  );

  return manuallySelectedStay ?? suggestedStay;
}

export function getStays(
  allAvailability: HotelBookableAndLockableOption[],
  numberOfNights: number,
  isHotelInItinerary: boolean,
  moneyFormanterFn: (amount: number, currency: string) => string,
  preferredDay?: Date
): Stay[] {
  const bookableOptions = getUniqueBookableOptions(allAvailability);

  return getStaysFromBookableOptions(
    bookableOptions,
    numberOfNights,
    isHotelInItinerary,
    moneyFormanterFn,
    preferredDay
  );
}

export function getUniqueBookableOptions(
  allBookableOptions: HotelBookableAndLockableOption[]
): HotelBookableAndLockableOption[] {
  const groupedByRoomType = groupBy(allBookableOptions, (bookableOption) =>
    bookableOption.rooms.map(getHotelBookableOptionRoomUniqueKey).join('-')
  );

  const uniqueBookableOptions = Object.values(groupedByRoomType).map(
    (availabilityByRoomType) => {
      const sortedByPrice = sortBy(
        availabilityByRoomType,
        (bookableOption) => bookableOption.totalPrice.amount
      );
      assertValue(
        sortedByPrice[0],
        'hotelDetailsStoreLogic.ts > getUniqueBookableOptions > sortedByPrice[0]'
      );
      return sortedByPrice[0];
    }
  );

  return uniqueBookableOptions.sort(bookableOptionComparer);
}

function getStaysFromBookableOptions(
  bookableOptions: HotelBookableAndLockableOption[],
  numberOfNights: number,
  isHotelInItinerary: boolean,
  moneyFormanterFn: (amount: number, currency: string) => string,
  preferredDay?: Date
): Stay[] {
  return bookableOptions.map((stay, index) => {
    const isFirstStay = index === 0;

    return {
      stayId: getHotelStayId(stay),
      rooms: mapRoomsToAddFromDateFromIsoFormat(stay.rooms),
      totalPriceMoney: stay.totalPrice,
      totalPrice: moneyFormanterFn(
        stay.totalPrice.amount,
        stay.totalPrice.currency
      ),
      grossPrice: stay.prices?.grossPrice
        ? moneyFormanterFn(
            stay.prices.grossPrice.amount,
            stay.prices.grossPrice.currency
          )
        : '',
      numberOfNights,
      isRefundable: stay.isRefundable,
      isFromMeta: stay.isFromMeta,
      imagesURL: stay.rooms
        .flatMap((room) => room.imageUrls)
        .map((imageUrl: string) => imageUrl as SmartSearchImageUrl),
      tag: preferredDay
        ? getHotelStayTag(stay.isRefundable, stay.rooms, preferredDay)
        : undefined,
      selectionType: getStaySelectionType(
        stay,
        isFirstStay,
        isHotelInItinerary
      ),
    };
  });
}

export function getStaySelectionType(
  stay: HotelBookableAndLockableOption,
  isFirstStay: boolean,
  isHotelInItinerary: boolean
): StaySelectionType | undefined {
  if (stay.isLocked) {
    return StaySelectionType.ManuallySelected;
  }

  if (stay.isFromMeta || (isHotelInItinerary && isFirstStay)) {
    return StaySelectionType.Suggested;
  }

  return StaySelectionType.None;
}

function getHotelStayId(stay: HotelBookableAndLockableOption): StayId {
  const rooms = stay.rooms;
  return {
    isRefundable: stay.isRefundable,
    hotelRoomIds: rooms.map((room) => ({
      roomCode: room.roomCode,
      boardBasis: room.boardBasis,
      roomTypeId: room.roomTypeId,
    })),
  };
}

function mapRoomsToAddFromDateFromIsoFormat(
  rooms: HotelBookableOptionRoom[]
): HotelRoom[] {
  function getRoomDescription(room: HotelBookableOptionRoom): {
    name: string;
    description: string;
  } {
    let name = room.description;
    let description = '';

    const newLineIndex = name.indexOf('\n-');

    if (newLineIndex > -1) {
      name = room.description.substring(0, newLineIndex);
      description = room.description.substring(newLineIndex);
    }

    return { name, description };
  }

  return rooms.map((room: HotelBookableOptionRoom): HotelRoom => {
    const displayedInfo = getRoomDescription(room);

    return {
      ...room,
      name: displayedInfo.name,
      description: displayedInfo.description,
      paxString: getFormattedPax([room.pax]),
      cancellationPolicies: mapCancellationPoliciesToAddFromDateFromIsoFormat(
        room.cancellationPolicies
      ),
    };
  });
}

function mapCancellationPoliciesToAddFromDateFromIsoFormat(
  cancellationPolicies: CancellationPolicy[]
): CancellationPolicy[] {
  return cancellationPolicies.map(
    (cancellationPolicy: CancellationPolicy): CancellationPolicy => ({
      fromDate: new SemboDate(cancellationPolicy.fromDateIso).formatUTC(
        DateFormat.CnxFormat
      ),
      fromDateIso: cancellationPolicy.fromDateIso,
      amount: cancellationPolicy.amount,
    })
  );
}

export function getFooterData(
  hotelDetails: HotelStaticData | undefined,
  selectedStay: Stay | undefined,
  cheapestStay: Stay | undefined,
  datesRangeFn: (ingredientId: IngredientId) => string | undefined,
  moneyFormanterFn: (amount: number, currency: string) => string
): HotelDetailsFooterData | undefined {
  function getHotelDetailsFooterPriceData(
    cheapestStay: Stay,
    isStaySelected: boolean,
    selectedStay?: Stay
  ): string {
    let result = '';

    if (!isStaySelected) {
      result += `${t('planner.from')} `;
    }

    const highlightedStay = selectedStay ?? cheapestStay;

    result += `${moneyFormanterFn(
      highlightedStay.totalPriceMoney.amount,
      highlightedStay.totalPriceMoney.currency
    )}`;

    return result;
  }

  if (!hotelDetails || !cheapestStay) {
    return;
  }

  const datesRange = datesRangeFn(hotelDetails.ingredientId);

  if (!datesRange) {
    return;
  }

  const isSelected =
    selectedStay?.selectionType === StaySelectionType.ManuallySelected;
  const translationKey = isSelected
    ? 'planner.change_room'
    : 'planner.see_rooms';

  return {
    priceInfo: getHotelDetailsFooterPriceData(
      cheapestStay,
      isSelected,
      selectedStay
    ),
    datesRange: datesRange,
    buttonText: t(translationKey),
    isStaySelected: isSelected,
  };
}

export function getGalleryData(
  images: ContentApiImage[] | SmartSearchImageUrl[] | undefined,
  header: string | undefined
): HotelDetailsGalleryData | undefined {
  if (!images || images.length <= 0) {
    return;
  }
  return {
    images,
    header,
  };
}
